import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { login as authSliceLogin } from "../../../redux/features/authSlice";
import { useLazyVerifyTokenQuery } from "../../../redux/services/auth";
import AuthPageWrapper from "../components/auth-page-wrapper";
import AuthErrorMessage from "../components/auth-error-message";
import AuthLink from "../components/auth-link";

type FlowMode = "login" | "signup";

type CallbackError = {
  message: string;
  reference?: string | null;
};

const sanitizeNextPath = (nextValue: string | null, mode: FlowMode): string => {
  const fallback = mode === "signup" ? "/news-agents/new" : "/";

  if (!nextValue) {
    return fallback;
  }

  if (!nextValue.startsWith("/") || nextValue.startsWith("//")) {
    return fallback;
  }

  return nextValue;
};

const mapErrorMessage = (errorCode: string | null, fallback: string | null) => {
  if (fallback) {
    return fallback;
  }

  switch (errorCode) {
    case "USER_ALREADY_EXISTS":
      return "An account with this email already exists. Please use Google login from the login page.";
    case "COMPANY_ALREADY_EXISTS":
      return "Your organization is already registered. Please contact your administrator for access.";
    case "NOT_FOUND":
      return "No account is linked to this Google profile yet. Please sign up first.";
    default:
      return "We could not complete Google authentication. Please try again.";
  }
};

export const AuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [verifyToken] = useLazyVerifyTokenQuery();
  const [error, setError] = useState<CallbackError | null>(null);

  const mode = useMemo<FlowMode>(
    () => (searchParams.get("mode") === "signup" ? "signup" : "login"),
    [searchParams]
  );
  const success = searchParams.get("success") === "true";
  const nextPath = sanitizeNextPath(searchParams.get("next"), mode);
  const supportReference = searchParams.get("reference");
  const errorCode = searchParams.get("errorCode");
  const errorMessage = searchParams.get("message");

  useEffect(() => {
    let active = true;

    const completeAuth = async () => {
      if (!success) {
        if (!active) return;

        setError({
          message: mapErrorMessage(errorCode, errorMessage),
          reference: supportReference,
        });
        return;
      }

      try {
        await verifyToken().unwrap();
        if (!active) return;

        dispatch(authSliceLogin());
        navigate(nextPath, { replace: true });
      } catch {
        if (!active) return;

        setError({
          message:
            "Google authentication completed, but we could not start your session. Please try logging in again.",
          reference: supportReference,
        });
      }
    };

    completeAuth();

    return () => {
      active = false;
    };
  }, [
    dispatch,
    navigate,
    nextPath,
    errorCode,
    errorMessage,
    success,
    supportReference,
    verifyToken,
  ]);

  return (
    <AuthPageWrapper
      title={mode === "signup" ? "Finishing your sign up" : "Finishing your login"}
      subtitle="Please wait while we complete your secure Google authentication"
    >
      {error ? (
        <div className="flex flex-column gap-4">
          <h1 className="text-2xl font-medium mb-1">Authentication issue</h1>
          <AuthErrorMessage error={error.message} />
          {error.reference ? (
            <small className="text-600">
              If this issue continues, share reference {error.reference} with our technical team.
            </small>
          ) : null}
          <div className="flex flex-column gap-2">
            <Button
              type="button"
              label="Go to login"
              onClick={() => navigate("/login", { replace: true })}
            />
            <AuthLink
              text="Need a new account? "
              linkText="Sign up"
              to="/sign-up"
              className="text-left"
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-column align-items-start gap-3">
          <ProgressSpinner style={{ width: "48px", height: "48px" }} />
          <h1 className="text-2xl font-medium m-0">
            {mode === "signup" ? "Creating your workspace..." : "Signing you in..."}
          </h1>
          <p className="m-0 text-700">This usually takes only a few seconds.</p>
        </div>
      )}
    </AuthPageWrapper>
  );
};

export default AuthCallbackPage;
